// askSmart-ui/src/pages/TOSP/components/TenantGrid.tsx
import React from "react";
import {
  formatSpan,
  spanBetween,
  tenancyEndDate,
  Tenant,
  tenantLeaseExpiry,
  todayISO,
} from "../types";
import { formatCurrency, formatDate } from "../format";
import { breakpointTone, MONTH_NAMES } from "../portal/portalSummary";
import type { PsfBand, TenantGroc } from "../portal/tenantGroc";
import { useReveal } from "../portal/useReveal";
import { useGridSort } from "../useGridSort";

type SortCol =
  | "operatingName"
  | "retailerType"
  | "primaryContactName"
  | "primaryContactEmail"
  | "primaryContactPhone"
  | "breakpointProgress"
  | "salesPsf"
  | "groc"
  | "leaseExpiry"
  | "tenancyEnded";

/**
 * How far a tenant is through their natural breakpoint this year.
 *
 * "No percentage rent on any lease" is its own state, not an absence: a blank
 * cell there would read as a tenant who has reported nothing, when in fact
 * there is nothing for them to report against.
 */
export type BreakpointProgress =
  | { state: "none" }
  | {
      state: "tracking";
      percent: number;
      /** The two figures behind it, for the tooltip. */
      label: string;
    };

/**
 * Sort key for the Lease column: NEGATIVE days remaining.
 *
 * The hook sorts a freshly clicked column ascending, and what is wanted here
 * is the longest lease first — so the key is inverted and ascending gives
 * most-time-left down to most-overdue. An expired lease keeps its days
 * positive, which puts it below everything still running and orders the
 * overruns worst-last. A lease with no date to work from sorts to the very
 * bottom rather than pretending to be either.
 */
const leaseSort = (tenant: Tenant): number => {
  const expiry = tenantLeaseExpiry(tenant);
  if (expiry.state === "unknown") return Number.POSITIVE_INFINITY;
  return expiry.state === "expired" ? expiry.days : -expiry.days;
};

/** Sort key for the column: anything untracked sits below every real figure. */
const progressSort = (progress: BreakpointProgress | null | undefined): number =>
  progress && progress.state === "tracking" ? progress.percent : -1;

/** Same rule for the two ratio columns: no figure sorts below every figure. */
const ratioSort = (value: number | null | undefined): number =>
  value === null || value === undefined ? -1 : value;

/**
 * Sales PSF bands borrow the Lease column's pills rather than defining their
 * own colours — green, amber and red are already spoken for there, and a second
 * set a shade off would read as a different meaning.
 */
const PSF_BAND_CLASS: Record<PsfBand, string> = {
  green: "ok",
  amber: "expiring",
  red: "expired",
};

const PSF_BAND_LABEL: Record<PsfBand, string> = {
  green: "above average for the centre",
  amber: "average for the centre",
  red: "below average for the centre",
};

/**
 * The window both ratios are measured over, written out.
 *
 * Always shown in full, because it is NOT the calendar year the Breakpoint
 * Progress column beside it uses — it ends at the last month the tenant
 * reported. Two adjacent columns on two different periods is exactly the kind
 * of thing that is misread in a hurry.
 */
const windowLabel = (groc: TenantGroc): string => {
  const from = `${MONTH_NAMES[groc.from.month - 1]} ${groc.from.year}`;
  const to = `${MONTH_NAMES[groc.to.month - 1]} ${groc.to.year}`;
  const gap = groc.monthsReported < 12 ? ` (${groc.monthsReported} of 12 months reported)` : "";
  return `${from} – ${to}${gap}`;
};

export interface TenantColumn {
  col: SortCol;
  label: string;
  /** Track width in the row's grid template. */
  width: string;
  /**
   * Shown behind a "?" beside the heading, the way the Configuration panels
   * explain themselves. For a column whose colours mean something — a reader
   * can see that a figure is green without being able to guess what green is.
   */
  help?: React.ReactNode;
  /** An empty spacer track inserted immediately after this column, as a grid
   *  width (e.g. "0.6fr"). Used to widen the blank space before the next one. */
  gutterAfter?: string;
}

/** The management view: the full tenant record at a glance. */
export const TENANT_COLUMNS: TenantColumn[] = [
  { col: "operatingName", label: "Tenant", width: "1.8fr" },
  { col: "retailerType", label: "Retailer Type", width: "1fr" },
  { col: "primaryContactName", label: "Primary Contact", width: "1.4fr" },
  { col: "primaryContactEmail", label: "Email", width: "1.8fr" },
  { col: "primaryContactPhone", label: "Phone", width: "1.2fr" },
];

/**
 * Past tenants: who they were, what they traded as, and when they left.
 *
 * No Lease column — the lease is over, so a countdown against its end date
 * would be a red pill on every row saying the same thing. The end date itself
 * is the column that matters here, and it carries how long ago.
 */
export const PAST_TENANT_COLUMNS: TenantColumn[] = [
  { col: "operatingName", label: "Tenant", width: "1.6fr" },
  { col: "tenancyEnded", label: "Tenancy Ended", width: "170px", gutterAfter: "0.3fr" },
  { col: "retailerType", label: "Business Type", width: "1fr" },
  { col: "primaryContactName", label: "Primary Contact", width: "1.3fr" },
  { col: "primaryContactEmail", label: "Email", width: "1.6fr" },
];

/** The overview: who the tenants are, and where their sales have got to. */
export const TENANT_SUMMARY_COLUMNS: TenantColumn[] = [
  // Trimmed from 2fr: a tenant name needs far less than that, and the surplus
  // was showing as a large empty stretch before Lease while the later columns
  // sat cramped. The freed width is spread across the flexible tracks below so
  // the columns read as evenly spaced.
  { col: "operatingName", label: "Tenant", width: "1.5fr" },
  // Fixed, and sized for the longest it goes: "+1,234 days" on a lease well
  // past its end. A flexible track would let the pill wrap mid-phrase.
  { col: "leaseExpiry", label: "Lease", width: "154px", gutterAfter: "0.6fr" },
  { col: "retailerType", label: "Business Type", width: "1.2fr" },
  // The widest of the flexible tracks: it's the only column here that plots
  // rather than states, and a bar too narrow to read its own fill defeats the
  // point of drawing one. Narrowing it for the two ratios did exactly that, so
  // it is back at full width with a gutter holding them off.
  {
    col: "breakpointProgress",
    label: "Breakpoint Progress",
    width: "2fr",
    gutterAfter: "0.4fr",
  },
  // The two figures a tenancy is judged by, in the order they're read: what
  // the space earns, then what it costs against that. Spaced apart so the pair
  // don't read as a single column of numbers.
  {
    col: "salesPsf",
    label: "Sales PSF",
    width: "0.9fr",
    gutterAfter: "0.4fr",
    // The colours rank a tenant against the rest of the centre, which is not
    // something a reader can infer from the figure in front of them.
    help: (
      <>
        <span className="tosp-grid-help-item">
          <span className="tosp-lease-flag tosp-psf-flag tosp-lease-ok">$$$</span>
          above average
        </span>
        <span className="tosp-grid-help-item">
          <span className="tosp-lease-flag tosp-psf-flag tosp-lease-expiring">$$</span>
          average
        </span>
        <span className="tosp-grid-help-item">
          <span className="tosp-lease-flag tosp-psf-flag tosp-lease-expired">$</span>
          below average
        </span>
      </>
    ),
  },
  { col: "groc", label: "GROC", width: "0.8fr" },
];

/** What a cell needs beyond the tenant record itself. */
interface CellContext {
  progressFor?: (tenant: Tenant) => BreakpointProgress | null;
  /** Required by the Sales PSF and GROC columns; omitted elsewhere. */
  grocFor?: (tenant: Tenant) => TenantGroc | null;
  /** Which quarter of the portfolio a tenant's Sales PSF falls in. */
  psfBandFor?: (tenant: Tenant) => PsfBand | null;
  /**
   * 0 → 1 while the grid reveals itself on mount.
   *
   * The bar and the figure beside it are both multiplied by it, so they climb
   * together by construction rather than by two timings being kept in step.
   */
  reveal: number;
}

// How each column reads. Kept beside the column definitions so a column can't
// be listed without a cell to render it.
const CELLS: Record<SortCol, (tenant: Tenant, ctx: CellContext) => React.ReactNode> = {
  // Both names in one column: the operating name is who the tenant is, the
  // legal entity is who the lease is with, and an admin chasing rent needs the
  // second without losing the first. Shown only where they differ, so the
  // common case where a company trades under its own name reads as one line.
  operatingName: (t) => (
    <div className="tosp-cell-stack">
      <span>{t.operatingName || t.legalEntity}</span>
      {t.legalEntity && t.legalEntity !== t.operatingName && t.operatingName && (
        <span className="tosp-cell-sub">{t.legalEntity}</span>
      )}
    </div>
  ),
  retailerType: (t) => t.retailerType,
  // The day the tenancy ended, and how long ago. Only ever asked for on the
  // Past Tenants tab, where a blank would be a contradiction — a record is on
  // that tab BECAUSE it has an end date — so a dash here means the dates
  // disagree with each other and is worth seeing.
  tenancyEnded: (t) => {
    const ended = tenancyEndDate(t);
    if (!ended) return <span className="tosp-progress-none">—</span>;
    const since = spanBetween(ended, todayISO());
    return (
      <div className="tosp-cell-stack">
        <span>{formatDate(ended)}</span>
        {since && <span className="tosp-cell-sub">{formatSpan(since)} ago</span>}
      </div>
    );
  },
  primaryContactName: (t) => t.primaryContactName,
  primaryContactEmail: (t) => <span className="tosp-cell-wrap">{t.primaryContactEmail}</span>,
  primaryContactPhone: (t) => t.primaryContactPhone,
  // Where the lease stands against its own end date. Silent until the notice
  // period opens, then a countdown, then a count-up once the date has passed.
  //
  // The two read differently on purpose: "expires in 12 days" is something to
  // do, "expired 40 days ago" is something already carried. A warning that
  // stopped at zero would vanish exactly when it began to matter.
  leaseExpiry: (t) => {
    const expiry = tenantLeaseExpiry(t);
    if (expiry.state === "unknown")
      return <span className="tosp-progress-none">—</span>;

    const expired = expiry.state === "expired";
    // Years and days, not a bare day count: "1,889 days" has to be divided in
    // the head before it means anything, while "5 years 64 days" is read. The
    // days stay on beside the years — once a lease is inside its final year
    // they are the whole story.
    const span = spanBetween(todayISO(), expiry.on);
    // Grouped, because a lease with years left reads as "2,498 days" and an
    // ungrouped 2498 is momentarily mistaken for a date or a rent figure.
    const count = expiry.days.toLocaleString();
    const unit = expiry.days === 1 ? "day" : "days";
    const text = span ? formatSpan(span) : `${count} ${unit}`;

    // The span alone in the cell — the column is scanned, not read — with the
    // date, the exact day count and the notice period a hover away for anyone
    // who wants them.
    return (
      <span
        className={`tosp-lease-flag tosp-lease-${expiry.state}`}
        title={
          expired
            ? `Lease ended ${expiry.on} — ${count} ${unit} ago`
            : `Lease ends ${expiry.on} — ${count} ${unit}, ${expiry.noticeDays} days' notice required`
        }
      >
        {expired ? `+${text}` : text}
      </span>
    );
  },

  breakpointProgress: (t, ctx) => {
    const progress = ctx.progressFor?.(t);
    // Percentage rent applies, but there is nothing to measure against yet —
    // a 0% bar would imply a year gone badly rather than one not started.
    if (!progress) return <span className="tosp-progress-none">—</span>;
    // No percentage rent on anything this tenant holds: nothing to track, as
    // distinct from nothing tracked so far.
    if (progress.state === "none")
      return (
        <span className="tosp-progress-none" title="No percentage rent on this lease">
          N/A
        </span>
      );

    return (
      <div className="tosp-progress-cell" title={progress.label}>
        <div className="tosp-progress-track">
          <div
            // Toned off the settled figure, not the climbing one, so a bar on
            // its way to green doesn't run red then amber to get there.
            className={`tosp-progress-fill tosp-progress-${breakpointTone(progress.percent)}`}
            style={{ width: `${Math.min(100, progress.percent) * ctx.reveal}%` }}
          />
        </div>
        <span className="tosp-progress-figure">
          {(progress.percent * ctx.reveal).toFixed(1)}%
        </span>
      </div>
    );
  },

  // Sales per square foot of COMMERCIAL area — storage and back-of-house are
  // excluded, because the ratio answers what the trading space earns. Whole
  // dollars in the cell, the exact figures in the tooltip: the column is
  // scanned against its neighbours rather than reconciled in place.
  //
  // Toned by where the figure sits in the portfolio — top quarter, middle half,
  // bottom quarter — in the Lease column's pill, so a colour means the same
  // thing in both places. A tenant with nothing to be ranked against (too few
  // figures in the centre to make quarters of) reads as a plain figure rather
  // than as a false middle.
  salesPsf: (t, ctx) => {
    const groc = ctx.grocFor?.(t);
    if (!groc || groc.salesPsf === null)
      return <span className="tosp-progress-none">—</span>;

    // Counts up on the same clock as the bar beside it, so the three columns
    // arrive together instead of the ratios appearing once the bars have
    // finished. The band is taken from the SETTLED figure, not the climbing
    // one, so a pill can't run through red on its way to green.
    const figure = `$${Math.round(groc.salesPsf * ctx.reveal).toLocaleString()}`;
    const title = `${formatCurrency(groc.sales)} over ${windowLabel(groc)}`;
    const band = ctx.psfBandFor?.(t) ?? null;
    if (!band) return <span title={title}>{figure}</span>;

    return (
      <span
        className={`tosp-lease-flag tosp-psf-flag tosp-lease-${PSF_BAND_CLASS[band]}`}
        title={`${title} — ${PSF_BAND_LABEL[band]}`}
      >
        {figure}
      </span>
    );
  },

  // Occupancy cost excluding HST against sales, over the same window.
  //
  // An asterisk while any statement line can't be calculated yet — CAMS, until
  // the budget's per-tenant allocation is built. The figure is real but
  // understated, and it marks itself rather than being quoted as complete. The
  // mark disappears on its own the day the missing line produces figures.
  groc: (t, ctx) => {
    const groc = ctx.grocFor?.(t);
    if (!groc || groc.groc === null)
      return <span className="tosp-progress-none">—</span>;
    const title =
      `${formatCurrency(groc.cost)} of occupancy cost, excluding HST, over ${windowLabel(groc)}` +
      (groc.incomplete
        ? ` — UNDERSTATED: ${groc.missingLabels.join(", ")} not yet calculated.`
        : ".");
    return (
      <span title={title}>
        {(groc.groc * 100 * ctx.reveal).toFixed(1)}%
        {groc.incomplete && "*"}
      </span>
    );
  },
};

interface TenantGridProps {
  tenants: Tenant[];
  emptyMessage: string;
  /** Defaults to the full management column set. */
  columns?: TenantColumn[];
  onView: (tenant: Tenant) => void;
  /** Omitted on read-only surfaces, which then show View alone. */
  onEdit?: (tenant: Tenant) => void;
  onDelete?: (tenant: Tenant) => void;
  /** Required by the Breakpoint Progress column; omitted elsewhere. */
  progressFor?: (tenant: Tenant) => BreakpointProgress | null;
  /** Required by the Sales PSF and GROC columns; omitted elsewhere. */
  grocFor?: (tenant: Tenant) => TenantGroc | null;
  /** Required by the Sales PSF column's colours; omitted elsewhere. */
  psfBandFor?: (tenant: Tenant) => PsfBand | null;
  /** An empty spacer track inserted before Actions, as a grid width (e.g.
   *  "0.6fr"). Opt-in per surface so it doesn't shift the management table. */
  actionsGutter?: string;
  /**
   * How wide the Actions column is. Defaults to the management table's three
   * buttons; a surface showing only View says so and gives the width back to
   * the columns that carry figures.
   */
  actionsWidth?: string;
}

// The tenant list. Managing it and simply reading it are the same table, so
// the columns and actions are passed in rather than the grid being written
// twice.
export default function TenantGrid({
  tenants,
  emptyMessage,
  columns = TENANT_COLUMNS,
  onView,
  onEdit,
  onDelete,
  progressFor,
  grocFor,
  psfBandFor,
  actionsGutter,
  actionsWidth = "1.5fr",
}: TenantGridProps) {
  // Which column's "?" is open, if any. One at a time: two keys open at once
  // would push the rows down twice for no reason.
  const [helpFor, setHelpFor] = React.useState<SortCol | null>(null);
  // Progress isn't a field on the tenant, so it's read through the same
  // function that renders it — sorted by the figure shown, with tenants that
  // have none sorting below zero rather than beside the ones at 0%.
  // The bars fill on arrival rather than appearing already full, the same way
  // the tenant portal reveals its own. One clock for every row, so they climb
  // together instead of each starting when it happens to render.
  const reveal = useReveal();

  const { sortButton, sortRows } = useGridSort<SortCol, Tenant>(
    "operatingName",
    (tenant, col) =>
      col === "breakpointProgress"
        ? progressSort(progressFor?.(tenant))
        : col === "salesPsf"
          ? ratioSort(grocFor?.(tenant)?.salesPsf)
          : col === "groc"
            ? ratioSort(grocFor?.(tenant)?.groc)
            : col === "leaseExpiry"
              ? leaseSort(tenant)
              : // ISO dates sort as text, and an empty string puts a record
                // whose dates contradict each other at the top where it can be
                // seen rather than at the bottom where it can't.
                col === "tenancyEnded"
                ? tenancyEndDate(tenant) || ""
                : tenant[col],
  );

  // The optional gutter is a real (empty) track before Actions, so the empty
  // placeholder cell below keeps Actions landing in the final track rather than
  // auto-flowing into the gutter.
  /**
   * Which grid track a column's heading sits in, 1-based.
   *
   * Counted rather than indexed: a column carrying a gutter contributes TWO
   * tracks, so the position in `columns` and the position in the grid are not
   * the same number once any gutter is in play.
   */
  const helpTrack = (target: SortCol): number => {
    let track = 1;
    for (const column of columns) {
      if (column.col === target) return track;
      track += column.gutterAfter ? 2 : 1;
    }
    return 1;
  };

  /**
   * Tenants with no percentage rent sit together at the foot of the list.
   *
   * Their Breakpoint Progress reads N/A — there is nothing to measure, as
   * against nothing measured yet — so they answer none of the questions this
   * screen is scanned for, and scattered through the rows they break up the
   * column that is being read. Alphabetical among themselves, whatever the
   * list above is sorted by, so a particular one is still findable.
   */
  const orderedTenants = (() => {
    const rows = sortRows(tenants);
    if (!progressFor) return rows;
    const untracked = (tenant: Tenant) => progressFor(tenant)?.state === "none";
    const name = (tenant: Tenant) => tenant.operatingName || tenant.legalEntity || "";
    return [
      ...rows.filter((tenant) => !untracked(tenant)),
      ...rows
        .filter(untracked)
        .sort((a, b) => name(a).localeCompare(name(b))),
    ];
  })();

  const gridTemplateColumns = [
    ...columns.flatMap((c) => (c.gutterAfter ? [c.width, c.gutterAfter] : [c.width])),
    ...(actionsGutter ? [actionsGutter] : []),
    actionsWidth,
  ].join(" ");

  return (
    <>
      <div className="tosp-grid-header" style={{ gridTemplateColumns }}>
        {columns.map(({ col, label, gutterAfter, help }) => (
          <React.Fragment key={col}>
            <div className="tosp-grid-heading">
              {sortButton(col, label)}
              {help && (
                <button
                  type="button"
                  className="tosp-help-toggle"
                  aria-expanded={helpFor === col}
                  title="What do the colours mean?"
                  onClick={() => setHelpFor((open) => (open === col ? null : col))}
                >
                  ?
                </button>
              )}
            </div>
            {gutterAfter && <div aria-hidden />}
          </React.Fragment>
        ))}
        {actionsGutter && <div aria-hidden />}
        <div>Actions</div>
      </div>

      {/* Under the header rather than floating over the rows: it is read once
          and dismissed, and a popover would cover the very figures it explains.
          Laid out on the same tracks as the grid so it opens under ITS OWN
          heading — a full-width strip would sit under the tenant names and
          read as though it explained those. */}
      {helpFor && columns.find((c) => c.col === helpFor)?.help && (
        <div className="tosp-grid-help-row" style={{ gridTemplateColumns }}>
          <div className="tosp-grid-help" style={{ gridColumn: `${helpTrack(helpFor)} / -1` }}>
            {columns.find((c) => c.col === helpFor)?.help}
          </div>
        </div>
      )}

      {tenants.length === 0 && <div className="tosp-grid-empty">{emptyMessage}</div>}

      {orderedTenants.map((tenant) => (
        <div key={tenant.id} className="tosp-grid-row" style={{ gridTemplateColumns }}>
          {columns.map(({ col, gutterAfter }) => (
            <React.Fragment key={col}>
              <div>{CELLS[col](tenant, { progressFor, grocFor, psfBandFor, reveal })}</div>
              {gutterAfter && <div aria-hidden />}
            </React.Fragment>
          ))}
          {actionsGutter && <div aria-hidden />}
          <div className="tosp-actions">
            <button
              className="tosp-btn-icon tosp-btn-view"
              title="View"
              onClick={() => onView(tenant)}
            >
              👁
            </button>
            {onEdit && (
              <button
                className="tosp-btn-icon tosp-btn-edit"
                title="Edit"
                onClick={() => onEdit(tenant)}
              >
                ✎
              </button>
            )}
            {onDelete && (
              <button
                className="tosp-btn-icon tosp-btn-delete"
                title="Delete"
                onClick={() => onDelete(tenant)}
              >
                🗑
              </button>
            )}
          </div>
        </div>
      ))}
    </>
  );
}
